import { useMutation } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { FormColumn } from "../schemas/column-schema"

import { api } from "@/lib/api-client"

import { useAIParserSession } from "./use-ai-parser-session"

type ParserJobStatus = "pending" | "running" | "completed" | "failed" | "cancelled"

type StartGenerateColumnsJobResponse = {
  success: boolean
  message: string
  session_id: string
  job_id: string
  status: ParserJobStatus
}

type JobStatusApiResponse = {
  job_id: string
  status: ParserJobStatus
  error_message: string | null
  result: unknown
}

const POLL_INTERVAL_MS = 2000
const MAX_WAIT_MS = 15 * 60 * 1000

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function startGenerateColumnsJob(sessionId: string): Promise<string> {
  const response = (await api.post("/parser/generate-columns", {
    session_id: sessionId,
  })) as StartGenerateColumnsJobResponse

  return response.job_id
}

async function getJobStatus(jobId: string): Promise<JobStatusApiResponse> {
  const queryParams = new URLSearchParams()
  queryParams.set("job_id", jobId)

  return api.get(`/parser/job-status?${queryParams.toString()}`) as Promise<JobStatusApiResponse>
}

function getGeneratedColumnsFromJobResult(result: unknown): FormColumn[] {
  if (!result || typeof result !== "object") return []

  const columns = (result as { columns?: unknown }).columns
  return Array.isArray(columns) ? (columns as FormColumn[]) : []
}

async function waitForGeneratedColumns(jobId: string): Promise<FormColumn[]> {
  const startTime = Date.now()

  while (Date.now() - startTime < MAX_WAIT_MS) {
    const status = await getJobStatus(jobId)

    if (status.status === "completed") {
      return getGeneratedColumnsFromJobResult(status.result)
    }

    if (status.status === "failed") {
      throw new Error(status.error_message || "Generate columns job failed")
    }

    if (status.status === "cancelled") {
      throw new Error("Generate columns job cancelled")
    }

    await sleep(POLL_INTERVAL_MS)
  }

  throw new Error("Timed out waiting for generate columns job")
}

type UseGetAiColumnsProps = {
  onSuccess?: (data: FormColumn[]) => void
}

export function useGetAiColumns({ onSuccess }: UseGetAiColumnsProps = {}) {
  const { t } = useTranslation()
  const { currentSessionId } = useAIParserSession()

  return useMutation({
    mutationFn: async () => {
      if (!currentSessionId) {
        throw new Error("Missing session id")
      }

      const jobId = await startGenerateColumnsJob(currentSessionId)
      return await waitForGeneratedColumns(jobId)
    },
    onSuccess: (data) => {
      toast.success(t("pages.ai_parser.toasts.ai_columns_added"))
      onSuccess?.(data)
    },
  })
}
